// src/context/OutreachContext.js
import React, {
  createContext,
  useContext,
  useState,
  useEffect
} from 'react';
import {
  doc,
  onSnapshot,
  setDoc,
  updateDoc,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';

const OutreachContext = createContext();
export function useOutreach() {
  return useContext(OutreachContext);
}

export function OutreachProvider({ children }) {
  const { user } = useAuth();
  const userId = user?.uid;
  const docRef = userId && doc(db, 'outreach', userId);

  const [calls, setCalls]   = useState([]);
  const [emails, setEmails] = useState([]);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);

  // 1) On mount (or user change), subscribe and create doc if missing
  useEffect(() => {
    if (!userId) {
      setCalls([]);
      setEmails([]);
      setVisits([]);
      setLoading(false);
      return;
    }

    const unsub = onSnapshot(
      docRef,
      snapshot => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          setCalls(data.calls  || []);
          setEmails(data.emails || []);
          setVisits(data.visits || []);
        } else {
          // first time ever: create the document with empty arrays
          setDoc(docRef, { calls: [], emails: [], visits: [] });
          setCalls([]);
          setEmails([]);
          setVisits([]);
        }
        setLoading(false);
      },
      err => {
        console.error('Failed to load outreach lists:', err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [userId]);

  // 2) Simple add/remove helpers that no longer reset everything
  async function addToList(type, business) {
    if (!userId) return;
    try {
      await updateDoc(docRef, {
        [type]: arrayUnion({
          fid: business.attributes.FID,
          attributes: business.attributes
        })
      });
    } catch (err) {
      console.error(`Error adding to ${type}:`, err);
    }
  }

  async function removeFromList(type, business) {
    if (!userId) return;
    try {
      await updateDoc(docRef, {
        [type]: arrayRemove({
          fid: business.attributes.FID,
          attributes: business.attributes
        })
      });
    } catch (err) {
      console.error(`Error removing from ${type}:`, err);
    }
  }

  return (
    <OutreachContext.Provider
      value={{
        calls,
        emails,
        visits,
        loading,
        addCall:    biz => addToList('calls', biz),
        addEmail:   biz => addToList('emails', biz),
        addVisit:   biz => addToList('visits', biz),
        removeCall:  biz => removeFromList('calls', biz),
        removeEmail: biz => removeFromList('emails', biz),
        removeVisit: biz => removeFromList('visits', biz),
      }}
    >
      {children}
    </OutreachContext.Provider>
  );
}
